import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Wifi,
  Trash2,
  LogOut,
  Lock,
  MessageCircle,
  AlertTriangle,
  Info,
  KeyRound,
} from 'lucide-react';
import TermsContent from '../components/TermsContent';

type SectionId = 'all' | 'finalizing' | 'guide' | 'terms' | 'generating' | 'reveal';

const SECTIONS: { id: Exclude<SectionId, 'all'>; label: string }[] = [
  { id: 'finalizing', label: 'Finalizing Payment' },
  { id: 'guide', label: 'Quick Guide' },
  { id: 'terms', label: 'Terms of Service' },
  { id: 'generating', label: 'Generating Code' },
  { id: 'reveal', label: 'Access Code' },
];

const guideCards = [
  {
    icon: Wifi,
    title: 'WiFi Access',
    desc: "Enjoy high-speed Wi-Fi from Westfield. Connect to '_Free Westfield Wi-Fi', then follow the instructions on the splash page to get online.",
  },
  {
    icon: Trash2,
    title: 'Keep it clean',
    desc: "No food or open drink containers inside the pod (water/coffee with a lid is ok!)\n\nPlease take any litter with you when leaving. A clean pod is a happy pod.",
  },
  {
    icon: LogOut,
    title: 'Ending session',
    desc: 'Important: You must use the link sent to your email, or the booking page that you will be redirected to, to end this booking.',
    action: (
      <div className="flex items-start gap-2 bg-amber-50 rounded-lg px-3 py-2 text-sm">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <span className="text-amber-700">
          Failure to click the link in your email will result in ongoing charges.
        </span>
      </div>
    ),
  },
  {
    icon: MessageCircle,
    title: 'Need help?',
    desc: 'Having trouble finding your pod or accessing the WiFi? Drop us a message directly on WhatsApp.',
    action: (
      <a
        href="https://wa.me/447347474059"
        target="_blank"
        rel="noopener noreferrer"
        className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl px-4 py-3 text-sm font-medium hover:bg-primary/90 transition-colors"
      >
        <MessageCircle className="w-4 h-4" />
        WhatsApp Support
      </a>
    ),
  },
  {
    icon: KeyRound,
    title: 'Entering your code',
    desc: "When you get your access code, tap the circular button on the handle to wake the keypad.\n\nOnce the keypad lights up, type in your access code and press # to unlock.\n\nThe circle will glow green to confirm the door is unlocked.",
  },
];

const GuideCard = ({
  icon: Icon,
  title,
  desc,
  action,
}: {
  icon: React.ElementType;
  title: string;
  desc: string;
  action?: React.ReactNode;
}) => (
  <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#153F36]/10">
    <div className="flex flex-col gap-4">
      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
        <Icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
      </div>
      <h3 className="font-heading text-xl font-bold text-foreground">{title}</h3>
      <p className="text-muted-foreground text-base leading-relaxed whitespace-pre-line">{desc}</p>
    </div>
    {action && <div className="mt-5">{action}</div>}
  </div>
);

// --- Screens ---
const FinalizingScreen = () => (
  <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
      <CheckCircle className="w-10 h-10 text-primary" strokeWidth={1.5} />
    </div>
    <h2 className="font-heading text-2xl text-primary font-bold mb-2">Payment Authorised</h2>
    <p className="text-muted-foreground">Securing your sanctuary...</p>
    <div className="mt-8 flex gap-1">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="w-2 h-2 rounded-full bg-primary"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
        />
      ))}
    </div>
  </div>
);

const GuideScreen = () => (
  <div className="px-4 pt-6 pb-6">
    <h2 className="font-heading text-xl font-bold text-foreground text-center mb-4">Quick Guide</h2>
    <div className="bg-primary/10 rounded-xl px-4 py-3 mb-4 flex items-start gap-2">
      <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
      <p className="text-sm text-primary">
        Your session charges will only begin once you complete the steps below.
      </p>
    </div>
    <div className="flex flex-col gap-4">
      {guideCards.map((card, i) => (
        <GuideCard key={i} {...card} />
      ))}
    </div>
    <div className="mt-6">
      <button className="w-full bg-primary text-primary-foreground rounded-2xl py-4 font-medium text-base">
        I'm Ready
      </button>
    </div>
  </div>
);

const TermsScreen = () => (
  <div className="px-4 pt-6 pb-6">
    <h2 className="font-heading text-xl font-bold text-foreground mb-4">Terms of Service</h2>
    <div className="text-sm max-h-[60vh] overflow-auto bg-white rounded-xl border border-[#153F36]/10 p-4">
      <TermsContent />
    </div>
    <div className="mt-4">
      <button className="w-full bg-primary text-primary-foreground rounded-xl py-3.5 font-medium">
        Accept & Continue
      </button>
    </div>
  </div>
);

const GeneratingScreen = () => (
  <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
    <motion.div
      className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-8"
      animate={{ scale: [1, 1.05, 1] }}
      transition={{ duration: 2, repeat: Infinity }}
    >
      <Lock className="w-8 h-8 text-primary" strokeWidth={1.5} />
    </motion.div>
    <h2 className="font-heading text-xl text-primary font-bold mb-2">Generating Access Code...</h2>
    <p className="text-muted-foreground text-sm">Connecting to secure lock</p>
  </div>
);

const RevealScreen = () => {
  const digits = '4827'.split('');
  return (
    <div className="flex flex-col items-center px-6 py-10 text-center">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
        <CheckCircle className="w-8 h-8 text-primary" strokeWidth={1.5} />
      </div>
      <h2 className="font-heading text-2xl text-primary font-bold">Access Granted</h2>
      <p className="text-muted-foreground">You're all set</p>
      <div className="flex justify-center gap-4 my-6">
        {digits.map((d, i) => (
          <span key={i} className="font-heading text-5xl text-primary font-bold">{d}</span>
        ))}
        <span className="font-heading text-5xl text-primary font-bold">#</span>
      </div>
      <p className="text-muted-foreground text-sm mb-6">Access Code</p>
      <a
        href="#"
        className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-3.5 font-medium"
      >
        Manage Your Session
      </a>
    </div>
  );
};

const SectionFrame = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <section className="mb-8">
    <div className="flex items-center gap-2 mb-3 px-1">
      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </span>
      <div className="flex-1 h-px bg-[#153F36]/10" />
    </div>
    <div className="bg-[#f9f8f5] rounded-3xl border border-[#153F36]/10 overflow-hidden shadow-sm">
      {children}
    </div>
  </section>
);

export default function ProvisionPreview() {
  const [view, setView] = useState<SectionId>('all');

  const renderSection = (id: Exclude<SectionId, 'all'>) => {
    switch (id) {
      case 'finalizing': return <FinalizingScreen />;
      case 'guide': return <GuideScreen />;
      case 'terms': return <TermsScreen />;
      case 'generating': return <GeneratingScreen />;
      case 'reveal': return <RevealScreen />;
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#f9f8f5] flex flex-col">
      {/* Top Bar */}
      <div className="flex-shrink-0 flex items-center justify-center h-14 border-b border-[#153F36]/5 bg-[#FDFBF7]">
        <span className="font-funnel font-semibold text-2xl text-primary">qwip</span>
      </div>

      {/* Preview banner + selector */}
      <div className="sticky top-0 z-10 bg-[#FDFBF7]/95 backdrop-blur border-b border-[#153F36]/10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <p className="text-xs text-muted-foreground mb-2 text-center">
            /provision-preview - static preview of the booking flow (no token needed)
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            <button
              onClick={() => setView('all')}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                view === 'all'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white border border-[#153F36]/15 text-foreground'
              }`}
            >
              All on one page
            </button>
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setView(s.id)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  view === s.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white border border-[#153F36]/15 text-foreground'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full max-w-2xl mx-auto px-4 py-6">
        {view === 'all'
          ? SECTIONS.map((s) => (
              <SectionFrame key={s.id} label={s.label}>
                {renderSection(s.id)}
              </SectionFrame>
            ))
          : (
            <SectionFrame label={SECTIONS.find((s) => s.id === view)!.label}>
              {renderSection(view as Exclude<SectionId, 'all'>)}
            </SectionFrame>
          )}
      </div>

      <footer className="flex-shrink-0 py-3 text-center bg-[#f9f8f5]">
        <span className="text-sm text-muted-foreground">© 2025 qwip. All rights reserved.</span>
      </footer>
    </div>
  );
}
